import {
	Action, Module, Mutation, VuexModule,
} from 'vuex-module-decorators';
import { Loan } from '@/loan/types/loan';
import { LoanHelpCenterTopicIds } from '@/loan/enums/loan-help-center-topic-ids';
import { LoanStatement } from '@/loan/types/loan-statement';
import { LoanRecurringPayment } from '@/loan/types/loan-recurring-payment';
import { LoanRecurringPaymentAvailableDates } from '@/loan/types/loan-recurring-payment-available-dates';
import { HelpCenterFIContactInfo } from '@/help-center/types/help-center-fi-contact-info';
import { LoanLinkedAccount } from '@/loan/types/loan-linked-account';
import { LoanDetails } from '@/loan/types/loan-details';
import { LoanTransactionWithDetails } from '@/loan/types/loan-transaction-with-details';
import { LoanTransactionStatusId } from '@/loan/enums/loan-transaction-status-id';
import { LoanHelper } from '@/loan/helper';
import { LOAN_MAX_DAYS_PAST_DUE } from '@/loan/constants/loan-max-days-past-due';
import { LoanFrequency } from '@/loan/enums/loan-frequency';
import { LoanStatus } from '@/loan/enums/loan-status';
import { LOAN_TAKE_BACK_MINIMUM_AMOUNT } from '@/loan/constants/loan-take-back-minimum-amount';
import { LoanDetailsPaymentBreakdown } from '@/loan/types/loan-details-payment-breakdown';
import { LoanDetailsPeriod } from '@/loan/types/loan-details-period';
import { LoanProjection } from '@/loan/types/loan-projection';
import { LoanAPIService } from '@/loan/services/api';
import { LoanProjectionRequestQuery } from '@/loan/types/loan-projection-request-query';
import { UtilHelper } from '@/util/helper';
import { LoanPaymentStatus } from '@/loan/enums/loan-payment-status';

@Module({
	namespaced: true,
	name: 'loan',
})
export default class LoanStore extends VuexModule {
	public isPageErrorShown = false;

	public isPageLoaderShown = false;

	public linkedAccounts: LoanLinkedAccount[] = [];

	public loan: Loan | null = null;

	public loanDetails: LoanDetails | null = null;

	public orderedHelpCenterFAQTopicIds = [
		LoanHelpCenterTopicIds.MANAGING_MY_LOAN,
	];

	public projection: LoanProjection | null = null;

	public recurringPayment: LoanRecurringPayment | null = null;

	public recurringPaymentAvailableDates: LoanRecurringPaymentAvailableDates | null = null;

	public statements: LoanStatement[] = [];

	public transactions: LoanTransactionWithDetails[] = [];

	get autopayPaymentFrequencyText(): string {
		if (this.recurringPayment && this.recurringPaymentAvailableDates) {
			const draftDateOneISOStringValue = LoanHelper.getAutopayInitialDraftDateOneISOString({
				availableDates: this.recurringPaymentAvailableDates,
				frequency: this.recurringPayment.frequency,
				recurringPayment: this.recurringPayment,
			});

			const draftDateTwoISOStringValue = LoanHelper.getAutopayDraftDateTwoISOString({
				availableDates: this.recurringPaymentAvailableDates,
				draftDateOneISOString: draftDateOneISOStringValue,
				frequency: this.recurringPayment.frequency,
				recurringPayment: this.recurringPayment,
			});

			const draftDateOne = UtilHelper.convertISOStringToDate(draftDateOneISOStringValue);
			const draftDateTwo = draftDateTwoISOStringValue
				? UtilHelper.convertISOStringToDate(draftDateTwoISOStringValue)
				: null;

			return LoanHelper.getAutopayPaymentFrequencyText({
				amount: Number(this.recurringPayment.paymentTotalByFrequency),
				draftDateOne,
				draftDateTwo,
				frequency: this.recurringPayment.frequency,
			});
		}

		return '';
	}

	get autopayFrequency(): LoanFrequency {
		return (this.loanDetails as LoanDetails).recurringPayment.frequency as LoanFrequency;
	}

	get autopayLinkedAccount(): LoanLinkedAccount | null {
		const accountId = this.loanDetails?.recurringPayment.currentAccountId;
		return this.linkedAccounts.find((acc) => acc.id === accountId) ?? null;
	}

	get autopayMinimum(): number {
		return Number((this.loanDetails as LoanDetails).recurringPayment.minimum ?? 0);
	}

	get autopayPaymentAmount(): number {
		return Number((this.loanDetails as LoanDetails).recurringPayment.paymentAmount ?? 0);
	}

	get autopayTotalAmount(): number {
		return Number((this.loanDetails as LoanDetails).recurringPayment.currentTotalPeriodPaymentAmount ?? 0);
	}

	get amountAvailableToTakeBack(): number {
		return Number((this.loanDetails as LoanDetails).general.availableToWithdraw);
	}

	get amountDue(): number {
		return Number((this.loanDetails as LoanDetails).general.remainingInstallmentDue);
	}

	get amountPastDue(): number {
		return Number((this.loanDetails as LoanDetails).general.installmentPastDue);
	}

	get balance(): number {
		return Number((this.loanDetails as LoanDetails).general.balance);
	}

	get calculatedAmountDue(): number {
		return LoanHelper.calculateAmountDueWithTransactions({
			transactions: this.pendingTransactions,
			amount: this.amountDue,
		});
	}

	get calculatedAmountPastDue(): number {
		return LoanHelper.calculateAmountDueWithTransactions({
			transactions: this.pendingTransactions,
			amount: this.amountPastDue,
		});
	}

	get currentPeriod(): LoanDetailsPeriod {
		const { periods } = (this.loanDetails as LoanDetails);
		const currentPeriod = LoanHelper.getCurrentPeriod(periods);
		return currentPeriod ?? periods[0];
	}

	get daysDelinquent(): number {
		return (this.loanDetails as LoanDetails).general.delinquentDays;
	}

	get fiContactInfo(): HelpCenterFIContactInfo {
		return {
			email: this.loan?.fiConfig.kloansConfig.supportContactEmail ?? '',
			name: this.loan?.fiConfig.displayName ?? '',
			phone: this.loan?.fiConfig.kloansConfig.supportContactPhone ?? '',
		};
	}

	get hasOutstandingFees(): boolean {
		return Number((this.loanDetails as LoanDetails).general.outstandingFees) > 0;
	}

	get interestRate(): number {
		return Number((this.loanDetails as LoanDetails).primaryTerms.interestRate);
	}

	get isAutopayOn(): boolean {
		return !!this.loanDetails?.recurringPayment.active;
	}

	get isMaxDaysPastDue(): boolean {
		return this.daysDelinquent >= LOAN_MAX_DAYS_PAST_DUE;
	}

	get isPastDue(): boolean {
		return LoanHelper.checkIsPastDue(this.loanDetails);
	}

	get isPastDueWithPendingTransactions(): boolean {
		return this.isPastDue && this.calculatedAmountPastDue > 0;
	}

	get isTakeBackActive(): boolean {
		return this.amountAvailableToTakeBack >= LOAN_TAKE_BACK_MINIMUM_AMOUNT;
	}

	get isTakeBackEnabled(): boolean {
		return !this.isPastDue && (this.loanDetails as LoanDetails).general.takeBacksEnabled;
	}

	get loanId(): string | undefined {
		return this.loan?.id;
	}

	get minimumMonthlyPayment(): number {
		return Number((this.loanDetails as LoanDetails).general.paymentDueAmount);
	}

	get outstandingFees(): number {
		return Number((this.loanDetails as LoanDetails).general.outstandingFees);
	}

	get nonPendingTransactions(): LoanTransactionWithDetails[] {
		return [...this.transactions.filter((t) => t.status !== LoanTransactionStatusId.PENDING)];
	}

	get paymentBreakdown(): LoanDetailsPaymentBreakdown {
		return (this.loanDetails as LoanDetails).paymentBreakdown as LoanDetailsPaymentBreakdown;
	}

	get paymentStatus(): LoanPaymentStatus {
		return LoanHelper.getPaymentStatus(this.loanDetails as LoanDetails, this.isAutopayOn);
	}

	get payoffAmount(): number {
		return Number((this.loanDetails as LoanDetails).general.payoffAmount);
	}

	get pendingTransactions(): LoanTransactionWithDetails[] {
		return [...this.transactions.filter((t) => t.status === LoanTransactionStatusId.PENDING)];
	}

	get postedTransactions(): LoanTransactionWithDetails[] {
		return [...this.transactions.filter((t) => t.status === LoanTransactionStatusId.POSTED)];
	}

	get startBalance(): number {
		return Number((this.loanDetails as LoanDetails).terms.initialPrincipal);
	}

	get statementsSortedByLatest(): LoanStatement[] {
		return UtilHelper.sortObjectsWithISOStrings({
			asc: false,
			objects: this.statements,
			prop: 'date',
		});
	}

	get status(): LoanStatus {
		return (this.loanDetails as LoanDetails).general.status as LoanStatus;
	}

	get totalPeriods(): number {
		return (this.loanDetails as LoanDetails).primaryTerms.termInMonths;
	}

	@Mutation
	public setIsPageLoaderShown(isShown: boolean): void {
		this.isPageLoaderShown = isShown;
	}

	@Mutation
	public setIsPageErrorShown(isShown: boolean): void {
		this.isPageErrorShown = isShown;
	}

	@Mutation
	public setLinkedAccounts(accounts: LoanLinkedAccount[]): void {
		this.linkedAccounts = accounts;
	}

	@Mutation
	public setLoan(loan: Loan): void {
		this.loan = loan;
	}

	@Mutation
	public setLoanDetails(loanDetails: LoanDetails): void {
		this.loanDetails = loanDetails;
	}

	@Mutation
	public setProjection(projection: LoanProjection): void {
		this.projection = projection;
	}

	@Mutation
	public setRecurringPayment(recurringPayment: LoanRecurringPayment | null): void {
		this.recurringPayment = recurringPayment;
	}

	@Mutation
	public setRecurringPaymentAvailableDates(dates: LoanRecurringPaymentAvailableDates): void {
		this.recurringPaymentAvailableDates = dates;
	}

	@Mutation
	public setStatements(statements: LoanStatement[]): void {
		this.statements = statements;
	}

	@Mutation
	public setTransactions(transactions: LoanTransactionWithDetails[]): void {
		this.transactions = transactions;
	}

	@Action({ rawError: true })
	public async getAndSetLinkedAccounts(): Promise<void> {
		const accounts = await LoanAPIService.getLinkedAccounts();

		this.setLinkedAccounts(accounts);
	}

	@Action({ rawError: true })
	public async getAndSetLoan(loanId: string): Promise<void> {
		const loans = await LoanAPIService.getLoans();
		const loan = loans.find((l) => l.id === loanId) as Loan;

		this.setLoan(loan);
	}

	@Action({ rawError: true })
	public async getAndSetLoanDetails(loanId: string): Promise<void> {
		const loanDetails = await LoanAPIService.getDetails(loanId);

		this.setLoanDetails(loanDetails);
	}

	@Action({ rawError: true })
	public async getAndSetProjection({
		loanId,
		query,
	}: {
		loanId: string;
		query?: LoanProjectionRequestQuery;
	}): Promise<void> {
		const projection = await LoanAPIService.getProjection(loanId, query);

		this.setProjection(projection);
	}
}
